'use client';

/**
 * Einzige Quelle für In-App-Benachrichtigungen im Admin. Einmal im Layout
 * gemountet → EINE Supabase-Realtime-Subscription + EIN State, den Glocke
 * (Mobile + Desktop) UND die Vollseite teilen. Per-User-Read (read kommt
 * pro Viewer aus der API). View-as-aware via useActiveTenant.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase/client';
import { useActiveTenant } from '@/hooks/useActiveTenant';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  message: string | null;
  related_resource_type: string | null;
  related_resource_id: string | null;
  read: boolean;
  created_at: string;
}

interface NotificationsContextValue {
  items: AppNotification[];
  unreadCount: number;
  loading: boolean;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { tenantId } = useActiveTenant();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setItems([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`/api/admin/notifications?tenantId=${tenantId}`);
      if (res.ok) {
        const json = await res.json();
        setItems(json.notifications ?? []);
        setUnreadCount(json.unreadCount ?? 0);
      }
    } catch {
      /* Glocke ist nicht kritisch */
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData();
  }, [fetchData]);

  // Eine Realtime-Subscription für die ganze App (Provider ist genau 1×).
  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase
      .channel(`notifications:${tenantId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'in_app_notifications', filter: `tenant_id=eq.${tenantId}` },
        () => void fetchData(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [tenantId, fetchData]);

  const markRead = useCallback(
    (id: string) => {
      setItems((prev) => {
        const target = prev.find((n) => n.id === id);
        if (!target || target.read) return prev;
        setUnreadCount((u) => Math.max(0, u - 1));
        return prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      });
      if (!tenantId) return;
      fetch('/api/admin/notifications', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, id }),
      }).catch(() => {});
    },
    [tenantId],
  );

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    if (!tenantId) return;
    fetch('/api/admin/notifications', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, all: true }),
    }).catch(() => {});
  }, [tenantId]);

  return (
    <NotificationsContext.Provider value={{ items, unreadCount, loading, markRead, markAllRead }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    // Fallback, falls außerhalb des Providers gerendert — verhindert Crash.
    return { items: [], unreadCount: 0, loading: false, markRead: () => {}, markAllRead: () => {} };
  }
  return ctx;
}
